import type {
  ArtifactRecord,
  ArtifactCaptureIntegrity,
  OffloadedToolResult,
  PiEvidenceStore,
  ToolResultEnvelope,
  ToolResultOffloadPolicy,
  ToolSymbolicResult,
} from "./types.js";
import { ApproximateModelTokenEstimator } from "@pi-mentis/pi-mentis-inference";

const ERROR_LINE = /(?:\berror\b|\bfailed\b|\bfatal\b|exception|traceback|\bE[A-Z]{2,}\b)/i;
const PATH_TOKEN = /(?:^|[\s"'`(])((?:\.{0,2}\/|\/)?[\w@.-]+(?:\/[\w@.-]+)+(?::\d+(?::\d+)?)?)/g;
const tokenEstimator = new ApproximateModelTokenEstimator();

export function toolResultTokenAccounting(original: string, modelVisible: string) {
  const originalTokens = tokenEstimator.count(original);
  const modelVisibleTokens = tokenEstimator.count(modelVisible);
  const avoidedModelTokens = Math.max(0, originalTokens - modelVisibleTokens);
  return {
    estimator: "approximate-model-v1" as const,
    originalTokens,
    modelVisibleTokens,
    avoidedModelTokens,
    // Compatibility aliases for existing telemetry consumers.
    retainedTokens: modelVisibleTokens,
    offloadedTokens: avoidedModelTokens,
  };
}

function unique(values: readonly string[], limit: number): string[] {
  return [...new Set(values.filter((value) => value !== ""))].slice(0, limit);
}

function detailNumber(details: unknown, name: string): number | null | undefined {
  if (typeof details !== "object" || details === null) return undefined;
  const value = (details as Record<string, unknown>)[name];
  return typeof value === "number" || value === null ? value : undefined;
}

function pathsFrom(input: Readonly<Record<string, unknown>>, text: string): string[] {
  const explicit = [input["path"], input["file"], input["filename"]].filter(
    (value): value is string => typeof value === "string",
  );
  const matches = [...text.matchAll(PATH_TOKEN)].map((match) => match[1] ?? "");
  return unique([...explicit, ...matches], 16);
}

export function classifyToolResult(
  byteLength: number,
  policy: ToolResultOffloadPolicy,
): OffloadedToolResult["mode"] {
  if (byteLength <= policy.inlineMaxBytes) return "inline";
  if (byteLength <= policy.truncateMaxBytes) return "truncated";
  return "artifact";
}

export function summarizeToolResult(
  envelope: ToolResultEnvelope,
  policy: ToolResultOffloadPolicy,
  artifact?: ArtifactRecord,
): ToolSymbolicResult {
  const bytes = Buffer.byteLength(envelope.text, "utf8");
  const lines = envelope.text.split(/\r?\n/);
  const keyErrors = unique(
    lines.filter((line) => ERROR_LINE.test(line)).map((line) => line.trim().slice(0, 500)),
    8,
  );
  const command =
    typeof envelope.input["command"] === "string" ? envelope.input["command"] : undefined;
  const durationMs =
    envelope.startedAt === undefined
      ? undefined
      : Math.max(0, envelope.completedAt - envelope.startedAt);
  const exitCode = detailNumber(envelope.details, "exitCode");
  const captureIntegrity: ArtifactCaptureIntegrity = envelope.captureIntegrity ?? {
    complete: true,
    lossy: false,
    capturedBytes: bytes,
  };
  return {
    tool: envelope.toolName,
    status: envelope.isError ? "failed" : "completed",
    ...(command === undefined ? {} : { command }),
    cwd: envelope.cwd,
    ...(exitCode === undefined ? {} : { exitCode }),
    ...(durationMs === undefined ? {} : { durationMs }),
    errorCount: lines.filter((line) => ERROR_LINE.test(line)).length,
    keyErrors,
    files: pathsFrom(envelope.input, envelope.text),
    ...(artifact === undefined ? {} : { artifactId: artifact.id }),
    truncated: bytes > policy.inlineMaxBytes || captureIntegrity.lossy,
    originalBytes: captureIntegrity.sourceReportedBytes ?? bytes,
    ...(bytes <= policy.inlineMaxBytes
      ? {}
      : { preview: Buffer.from(envelope.text).subarray(0, policy.previewBytes).toString("utf8") }),
    captureIntegrity,
  };
}

export async function offloadToolResult(
  evidence: PiEvidenceStore,
  episodeId: string,
  eventId: string,
  envelope: ToolResultEnvelope,
  policy: ToolResultOffloadPolicy,
): Promise<OffloadedToolResult> {
  const bytes = Buffer.byteLength(envelope.text, "utf8");
  const mode = classifyToolResult(bytes, policy);
  if (mode === "inline") {
    return {
      mode,
      symbolic: summarizeToolResult(envelope, policy),
      modelText: envelope.text,
      tokenAccounting: toolResultTokenAccounting(envelope.text, envelope.text),
    };
  }
  const artifact = await evidence.writeArtifact({
    episodeId,
    eventId,
    toolCallId: envelope.toolCallId,
    mediaType: "text/plain; charset=utf-8",
    content: envelope.text,
    captureIntegrity: {
      complete: envelope.captureIntegrity?.complete ?? true,
      lossy: envelope.captureIntegrity?.lossy ?? false,
      capturedBytes: envelope.captureIntegrity?.capturedBytes ?? bytes,
      storedBytes: bytes,
      ...(envelope.captureIntegrity?.truncationStage === undefined
        ? {}
        : { truncationStage: envelope.captureIntegrity.truncationStage }),
    },
  });
  const symbolic = summarizeToolResult(envelope, policy, artifact);
  const headerSymbolic =
    mode === "truncated"
      ? Object.fromEntries(Object.entries(symbolic).filter(([key]) => key !== "preview"))
      : symbolic;
  const header = `<pi-mentis-tool-result artifact_id="${artifact.id}" mode="${mode}">\n${JSON.stringify(headerSymbolic, null, 2)}\n</pi-mentis-tool-result>`;
  const modelText =
    mode === "truncated" ? `${header}\n\nPreview:\n${symbolic.preview ?? ""}` : header;
  return {
    mode,
    symbolic,
    modelText,
    tokenAccounting: toolResultTokenAccounting(envelope.text, modelText),
    artifact,
  };
}
